import React from "react";

const formatScore = (value) => {
    const score = Number(value);
    return Number.isFinite(score) ? score.toFixed(1) : value || "--";
};

const LeagueDashboard = ({
    standings,
    awards,
    boxScores,
    week,
    loading,
    onOpenTab,
    isTeamScoreOnly,
}) => {
    const playoffTeams = Array.isArray(standings)
        ? standings.filter((team) => team.isPlayoffTeam)
        : [];
    const recentMatchups = Array.isArray(boxScores)
        ? [...boxScores]
              .sort((left, right) => {
                  const leftSpread = Math.abs(Number(left.home_score) - Number(left.away_score));
                  const rightSpread = Math.abs(Number(right.home_score) - Number(right.away_score));
                  const safeLeftSpread = Number.isFinite(leftSpread) ? leftSpread : Number.POSITIVE_INFINITY;
                  const safeRightSpread = Number.isFinite(rightSpread) ? rightSpread : Number.POSITIVE_INFINITY;
                  return safeLeftSpread - safeRightSpread;
              })
              .slice(0, 2)
        : [];
    const bestAwards = awards?.bestAwards?.slice(0, 2) || [];
    const worstAwards = awards?.worstAwards?.slice(0, 2) || [];
    const awardCards = [...bestAwards, ...worstAwards].slice(0, 4);

    if (loading) {
        return (
            <div className="rounded-xl border border-border bg-surface px-5 py-8 text-sm font-semibold text-text-muted shadow-sm">
                Loading league dashboard...
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(17rem,0.75fr)]">
                <div className="rounded-xl border border-border-card bg-surface p-5 shadow-sm sm:p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-xs font-extrabold uppercase tracking-widest text-green">
                                League dashboard
                            </p>
                            <h2 className="mt-2 font-display text-2xl font-black text-forest">
                                Playoff picture
                            </h2>
                            <p className="mt-1 text-sm font-semibold text-text-muted">
                                Teams currently inside the playoff line through Week {week}.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpenTab("standings")}
                            className="shrink-0 text-xs font-extrabold uppercase tracking-wide text-green hover:text-forest"
                        >
                            Full standings
                        </button>
                    </div>

                    {playoffTeams.length ? (
                        <div className="mt-5 overflow-hidden rounded-lg border border-border">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-forest text-[0.65rem] font-extrabold uppercase tracking-widest text-white">
                                    <tr>
                                        <th className="px-3 py-2">Team</th>
                                        <th className="px-3 py-2 text-right">W-L-T</th>
                                        <th className="px-3 py-2 text-right">PF</th>
                                        <th className="hidden px-3 py-2 text-right sm:table-cell">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {playoffTeams.map((team, index) => (
                                        <tr key={team.team_id || `${team.owner}-${team.team}-${index}`} className="border-t border-border/70">
                                            <td className="px-3 py-3">
                                                <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-light text-[0.65rem] font-extrabold text-green">
                                                    {team.standing || index + 1}
                                                </span>
                                                <span className="font-bold text-forest">{team.owner || team.team}</span>
                                                {team.owner && team.team ? (
                                                    <span className="block pl-7 text-xs font-semibold text-text-muted">{team.team}</span>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-3 text-right font-mono text-xs font-bold">
                                                {team.wins}-{team.losses}-{team.ties}
                                            </td>
                                            <td className="px-3 py-3 text-right font-mono text-xs font-bold">{team.pointsFor}</td>
                                            <td className="hidden px-3 py-3 text-right sm:table-cell">
                                                <span className={team.isByeTeam ? "text-xs font-extrabold uppercase text-mustard" : "text-xs font-extrabold uppercase text-green"}>
                                                    {team.isByeTeam ? "Bye" : "Playoff"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="mt-5 rounded-lg border border-dashed border-border bg-bg px-4 py-5 text-sm font-semibold text-text-muted">
                            Playoff positions will appear once standings are available.
                        </p>
                    )}
                </div>

                <div className="rounded-xl border border-border-card bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-extrabold uppercase tracking-widest text-mustard">Week {week} board</p>
                                {recentMatchups.length > 0 && (
                                    <span className="rounded-full bg-green-light px-2 py-0.5 text-[0.6rem] font-extrabold uppercase tracking-wide text-green">
                                        {recentMatchups.length} featured
                                    </span>
                                )}
                            </div>
                            <h2 className="mt-2 font-display text-xl font-black text-forest">Latest scores</h2>
                            <p className="mt-1 text-xs font-semibold text-text-muted">
                                A quick read on this week&apos;s matchups.
                            </p>
                        </div>
                        <button type="button" onClick={() => onOpenTab("boxscores")} className="shrink-0 text-xs font-extrabold uppercase tracking-wide text-green hover:text-forest">
                            View box scores <span aria-hidden="true">→</span>
                        </button>
                    </div>
                    <div className="mt-5 space-y-3">
                        {recentMatchups.length ? recentMatchups.map((game, index) => {
                            const homeScore = Number(game.home_score);
                            const awayScore = Number(game.away_score);
                            const hasScores = Number.isFinite(homeScore) && Number.isFinite(awayScore);
                            const isTie = hasScores && homeScore === awayScore;
                            const homeWon = hasScores && homeScore > awayScore;
                            const awayWon = hasScores && awayScore > homeScore;
                            const margin = hasScores ? Math.abs(homeScore - awayScore) : null;
                            const total = hasScores ? homeScore + awayScore : null;
                            const winner = homeWon
                                ? game.home_owner || game.home_team
                                : awayWon
                                  ? game.away_owner || game.away_team
                                  : null;
                            const outcome = game.is_final === false
                                ? "In progress"
                                : isTie
                                  ? "Tied"
                                  : winner
                                    ? `${winner} won by ${formatScore(margin)}`
                                    : "Score pending";
                            const status = game.is_final === false ? "Live" : isTie ? "Tie" : "Final";
                            const homePrimary = game.home_owner || game.home_team;
                            const awayPrimary = game.away_owner || game.away_team;
                            const homeSecondary = game.home_owner && game.home_team
                                ? game.home_team
                                : null;
                            const awaySecondary = game.away_owner && game.away_team
                                ? game.away_team
                                : null;
                            return (
                                <article
                                    key={`${game.home_team_id || game.home_owner}-${game.away_team_id || game.away_owner}-${index}`}
                                    className="overflow-hidden rounded-lg border border-border bg-surface"
                                >
                                    <div className="flex items-center justify-between border-b border-border px-3 py-2">
                                        <span className="text-[0.6rem] font-extrabold uppercase tracking-widest text-text-muted">
                                            Matchup {index + 1}
                                        </span>
                                        <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-extrabold uppercase tracking-wide ${
                                            game.is_final === false
                                                ? "bg-coral-light text-coral"
                                                : isTie
                                                  ? "bg-mustard-light text-mustard"
                                                  : "bg-green-light text-green"
                                        }`}>
                                            {status}
                                        </span>
                                    </div>
                                    <div className="space-y-1 px-3 py-2">
                                        {[
                                            {
                                                primary: homePrimary,
                                                secondary: homeSecondary,
                                                score: game.home_score,
                                                won: homeWon,
                                            },
                                            {
                                                primary: awayPrimary,
                                                secondary: awaySecondary,
                                                score: game.away_score,
                                                won: awayWon,
                                            },
                                        ].map((team, teamIndex) => (
                                            <div
                                                key={`${team.primary || "team"}-${teamIndex}`}
                                                className={`flex items-center justify-between gap-3 rounded-md px-2 py-1.5 ${team.won ? "bg-green-light/60" : ""}`}
                                            >
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${team.won ? "bg-green-mid" : "bg-border"}`} />
                                                    <div className="min-w-0">
                                                        <p className={`truncate text-sm ${team.won ? "font-extrabold text-forest" : "font-bold text-text"}`}>
                                                            {team.primary || "Team"}
                                                        </p>
                                                        {team.secondary && (
                                                            <p className="truncate text-[0.65rem] font-semibold text-text-muted">
                                                                {team.secondary}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <strong className={`font-mono text-base ${team.won ? "text-green" : "text-text-muted"}`}>
                                                    {formatScore(team.score)}
                                                </strong>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2 text-[0.65rem] font-bold text-text-muted">
                                        <span>{outcome}</span>
                                        {total !== null && <span className="font-mono">Total {formatScore(total)}</span>}
                                    </div>
                                </article>
                            );
                        }) : <p className="py-5 text-sm font-semibold text-text-muted">No completed matchups available.</p>}
                    </div>
                </div>
            </section>

            <section className="rounded-xl border border-border-card bg-white p-5 shadow-sm sm:p-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs font-extrabold uppercase tracking-widest text-mustard">Week {week}</p>
                        <h2 className="mt-1 font-display text-xl font-black text-forest">Weekly awards</h2>
                    </div>
                    <button type="button" onClick={() => onOpenTab("awards")} className="text-xs font-extrabold uppercase tracking-wide text-green hover:text-forest">
                        All awards
                    </button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {awardCards.length ? awardCards.map(([category, owner, value], index) => (
                        <div key={`${category}-${owner}-${index}`} className="rounded-lg border border-border bg-surface p-3">
                            <p className="text-[0.65rem] font-extrabold uppercase tracking-widest text-text-muted">{String(category).replace(/:\s*$/, "")}</p>
                            <p className="mt-2 font-bold text-forest">{owner}</p>
                            {value ? <p className="mt-1 font-mono text-xs font-bold text-text-muted">{value}</p> : null}
                        </div>
                    )) : <p className="text-sm font-semibold text-text-muted">Weekly awards will appear when available.</p>}
                </div>
            </section>

            {!isTeamScoreOnly ? null : (
                <p className="text-xs font-semibold text-text-muted">
                    Matchup views use team scores for this league's data profile.
                </p>
            )}
        </div>
    );
};

export default LeagueDashboard;
